import { sumPlatformQueryCredits, type SimulationPlatform } from '@lensora/shared'
import { Button, Card, CardContent, cn } from '@lensora/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Zap, Play, Pause, RefreshCw, CheckCircle,
  Loader2, AlertTriangle, Calendar,
  Activity, Timer, DollarSign,
} from 'lucide-react'
import * as React from 'react'

import { formatPlatformName, isGrowthGatedPlatform, PICKER_PLATFORMS } from '@/components/dashboard/PlatformIcon'
import { useGrowthPlus } from '@/hooks/use-growth-plus'
import { useMe } from '@/hooks/use-me'
import { useTrendingPrompts } from '@/hooks/use-trending-queries'
import { api } from '@/lib/api'

// ── Types ──────────────────────────────────────────────────────────────────────

interface AutomationSchedule {
  id: string
  is_active: boolean
  run_time: string
  platforms: string[]
  prompt_ids: string[]
  credits_per_run: number | null
  last_run_at: string | null
  next_run_at: string | null
  paused_reason: string | null
}

interface AutomationStats {
  total_runs: number
  prompts_processed: number
  credits_used: number
  last_run_at: string | null
}

interface AutomationRun {
  id: string
  prompts_run: number
  credits_used: number
  status: 'completed' | 'partial' | 'failed'
  ran_at: string
}

interface TrackedPrompt {
  id: string
  query_text: string
  platforms: string[]
}

interface AutomationData {
  schedule: AutomationSchedule | null
  stats: AutomationStats
  prompts: TrackedPrompt[]
}

interface CreditPools {
  automation?: { prompts_per_day?: number; plan_active?: boolean }
}

// ── Constants ──────────────────────────────────────────────────────────────────

// Pill accent colours only — tuned for this page's selected/unselected
// button styling, distinct from PlatformIcon's icon-badge colours. Typed
// against every SimulationPlatform so a new platform fails to compile here
// until it's given an accent, instead of silently missing from the list.
const PLATFORM_ACCENT: Record<SimulationPlatform, { color: string; bg: string }> = {
  chatgpt:          { color: '#10A37F', bg: 'rgba(16,163,127,0.12)' },
  claude:           { color: '#C96442', bg: 'rgba(201,100,66,0.12)' },
  perplexity:       { color: '#20B8CD', bg: 'rgba(32,184,205,0.12)' },
  gemini:           { color: '#4285F4', bg: 'rgba(66,133,244,0.12)' },
  'google-ai':      { color: '#34A853', bg: 'rgba(52,168,83,0.12)' },
  'chatgpt-search': { color: '#0EA5E9', bg: 'rgba(14,165,233,0.12)' },
  'claude-search':  { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
}

const PLATFORMS = PICKER_PLATFORMS.map((id) => ({
  id,
  label: formatPlatformName(id),
  growthOnly: isGrowthGatedPlatform(id),
  ...PLATFORM_ACCENT[id],
}))

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const label = i === 0 ? '12:00am' : i < 12 ? `${i}:00am` : i === 12 ? '12:00pm' : `${i - 12}:00pm`
  const value = `${String(i).padStart(2, '0')}:00`
  return { label, value }
})

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatRelative(iso: string | null): string {
  if (!iso) return 'Never'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Never'
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
    ' at ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

// ── Step card wrapper ──────────────────────────────────────────────────────────

function StepCard({ step, title, subtitle, accent, children }: {
  step: number; title: string; subtitle: string; accent: string; children: React.ReactNode
}) {
  return (
    <div className="flex flex-col rounded-xl border border-border bg-surface overflow-hidden">
      <div className="h-1 w-full" style={{ background: accent }} />
      <div className="flex items-start gap-3 border-b border-border px-5 py-4">
        <div
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
          style={{ background: accent }}
        >
          {step}
        </div>
        <div>
          <p className="text-sm font-bold text-text">{title}</p>
          <p className="text-[11px] text-text-muted">{subtitle}</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-5">{children}</div>
    </div>
  )
}

function RunStatusBadge({ status }: { status: 'completed' | 'partial' | 'failed' }) {
  const map = {
    completed: { label: 'Done',    cls: 'bg-feedback-success/10 text-feedback-success' },
    partial:   { label: 'Partial', cls: 'bg-feedback-warning/10 text-feedback-warning' },
    failed:    { label: 'Failed',  cls: 'bg-feedback-error/10 text-feedback-error'   },
  }
  const s = map[status]
  return <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', s.cls)}>{s.label}</span>
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function AutomationPage() {
  const queryClient = useQueryClient()
  const isGrowthPlus = useGrowthPlus()
  const { data: me, isLoading: meLoading } = useMe()
  // Client-role users (the agency's own customers) can view the schedule and
  // history but can't commit the tenant's credit balance to a recurring
  // spend — the API already 403s these; disable the controls up front so it
  // never gets that far.
  const isClientRole = me?.user.role === 'client'
  // Self-serve customers (client-role users in the shared app tenant) own the
  // credit pool automation spends from and manage their own automation;
  // client-role users in dedicated agency tenants don't — mirrors
  // components/sidebar.tsx's isSelfServeClient.
  const isSelfServeClient = isClientRole && me?.tenant?.slug === 'app'
  // Keep controls disabled until we actually know the role, so there's no
  // brief fail-open window while useMe() is still loading.
  const canWriteAutomation = !meLoading && (!isClientRole || isSelfServeClient)

  const [runTime, setRunTime] = React.useState('06:00')
  const [selectedPlatforms, setSelectedPlatforms] = React.useState<string[]>(
    PICKER_PLATFORMS.filter((p) => !isGrowthGatedPlatform(p))
  )
  const [selectedPromptIds, setSelectedPromptIds] = React.useState<Set<string>>(new Set())
  const [promptTab, setPromptTab] = React.useState<'scheduled' | 'trending'>('scheduled')
  const [saveSuccess, setSaveSuccess] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [initialised, setInitialised] = React.useState(false)

  const { data: mainData, isLoading: mainLoading } = useQuery({
    queryKey: ['automation'],
    queryFn: () => api.get<AutomationData>('/api/automation'),
    staleTime: 30_000,
  })

  const { data: historyData, isLoading: historyLoading, refetch: refetchHistory } = useQuery({
    queryKey: ['automation-history'],
    queryFn: () => api.get<{ runs: AutomationRun[] }>('/api/automation/history'),
    staleTime: 30_000,
  })

  const { data: poolsData } = useQuery({
    queryKey: ['credit-pools'],
    queryFn: () => api.get<CreditPools>('/api/credits/pools'),
    staleTime: 60_000,
  })

  const { data: trendingData, isLoading: trendingLoading } = useTrendingPrompts(null, promptTab === 'trending')

  const schedule = mainData?.schedule ?? null
  const stats = mainData?.stats ?? null
  const availablePrompts = mainData?.prompts ?? []
  const runs = historyData?.runs ?? []
  const automationPerDay = poolsData?.automation?.prompts_per_day ?? 0
  const planActive = poolsData?.automation?.plan_active ?? false

  React.useEffect(() => {
    if (initialised || !mainData) return
    if (schedule) {
      setRunTime(schedule.run_time ?? '06:00')
      setSelectedPlatforms(schedule.platforms ?? ['chatgpt', 'claude', 'perplexity', 'gemini'])
      setSelectedPromptIds(new Set(schedule.prompt_ids ?? []))
    }
    setInitialised(true)
  }, [mainData, schedule, initialised])

  const saveMutation = useMutation({
    mutationFn: (body: { run_time: string; platforms: string[]; prompt_ids: string[]; credits_per_run: number }) =>
      api.post<{ schedule: AutomationSchedule }>('/api/automation', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation'] })
      setSaveSuccess(true)
      setSaveError(null)
      setTimeout(() => setSaveSuccess(false), 2500)
    },
    onError: (err: unknown) => {
      const e = err as { message?: string }
      setSaveError(e.message ?? 'Failed to save settings.')
    },
  })

  const toggleMutation = useMutation({
    mutationFn: (isActive: boolean) =>
      api.post<{ schedule: AutomationSchedule }>('/api/automation/toggle', { is_active: isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation'] })
      setSaveError(null)
    },
    onError: (err: unknown) => {
      const e = err as { message?: string }
      setSaveError(e.message ?? 'Failed to update automation status.')
    },
  })

  const isActive = schedule?.is_active ?? false
  const scansPerRun = selectedPromptIds.size * selectedPlatforms.length
  const estimatedMonthly = scansPerRun * 30
  // Distinct from scansPerRun (a count) — chatgpt-search/claude-search cost
  // more than 1 credit per scan, so "credits/day" and the actual amount
  // billed at run time need the real per-platform cost, not just a count.
  const creditsPerRun = selectedPromptIds.size * sumPlatformQueryCredits(selectedPlatforms)

  function togglePlatform(id: string) {
    if (isGrowthGatedPlatform(id) && !isGrowthPlus) return
    setSelectedPlatforms((prev) => prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id])
  }
  function togglePrompt(id: string) {
    setSelectedPromptIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function selectAll() { setSelectedPromptIds(new Set(availablePrompts.map((p) => p.id))) }
  function deselectAll() { setSelectedPromptIds(new Set()) }
  function handleSave() {
    setSaveError(null)
    saveMutation.mutate({ run_time: runTime, platforms: selectedPlatforms, prompt_ids: Array.from(selectedPromptIds), credits_per_run: creditsPerRun })
  }

  if (mainLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 animate-pulse rounded-lg bg-surface-raised" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map((i) => <div key={i} className="h-96 animate-pulse rounded-xl border border-border bg-surface" />)}
        </div>
      </div>
    )
  }

  // ── Upsell (no automation plan) ───────────────────────────────────────────────

  if (!planActive && automationPerDay === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-text">Automation</h2>
          <p className="mt-1 text-sm text-text-muted">Run your tracked prompts automatically every day across all AI platforms.</p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-brand-accent/10">
              <Zap className="h-7 w-7 text-brand-accent" />
            </div>
            <h3 className="mb-2 text-xl font-bold text-text">Unlock daily automation</h3>
            <p className="mb-6 max-w-sm text-sm text-text-muted">
              Automated daily scans are included in paid plans. Upgrade to run your prompts every day without lifting a finger.
            </p>
            <Button asChild>
              <a href="/dashboard/settings?tab=billing">
                <Zap className="mr-2 h-4 w-4" />
                View plans
              </a>
            </Button>
            <p className="mt-4 text-xs text-text-muted">Starter: 20/day · Growth: 50/day · Enterprise: 100/day</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold text-text">
            Automation
            <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-semibold',
              isActive ? 'bg-feedback-success/10 text-feedback-success' : 'bg-surface-raised text-text-muted'
            )}>
              {isActive ? 'Running daily' : 'Paused'}
            </span>
          </h2>
          <p className="mt-1 text-sm text-text-muted">Run your tracked prompts automatically every day across all AI platforms.</p>
        </div>
      </div>

      {saveError && (
        <div className="flex items-center gap-2 rounded-lg border border-feedback-error/30 bg-feedback-error/10 px-4 py-3 text-sm text-feedback-error">
          <AlertTriangle className="h-4 w-4 shrink-0" />{saveError}
        </div>
      )}

      {/* 3-column wizard */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" style={{ minHeight: 520 }}>

        {/* Step 1: Choose Prompts */}
        <StepCard
          step={1}
          title="Choose Prompts"
          subtitle="Select the queries you want to track automatically"
          accent="linear-gradient(135deg, #6366f1, #8b5cf6)"
        >
          <div className="flex flex-col h-full">
            {/* Tabs */}
            <div className="mb-3 flex rounded-lg border border-border bg-surface-raised p-0.5">
              {(['scheduled', 'trending'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setPromptTab(tab)}
                  className={cn(
                    'flex-1 rounded-md py-1.5 text-xs font-semibold capitalize transition-all',
                    promptTab === tab
                      ? 'bg-surface text-text shadow-sm'
                      : 'text-text-muted hover:text-text'
                  )}
                >
                  {tab === 'scheduled' ? 'Scheduled' : 'Trending'}
                </button>
              ))}
            </div>

            {promptTab === 'scheduled' ? (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs text-text-muted">{availablePrompts.length} prompts</span>
                  <div className="flex items-center gap-2 text-xs">
                    <button onClick={selectAll} className="font-semibold text-brand-accent hover:underline">Select all</button>
                    <span className="text-border">·</span>
                    <button onClick={deselectAll} className="text-text-muted hover:underline">Deselect all</button>
                  </div>
                </div>
                {availablePrompts.length === 0 ? (
                  <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
                    <Zap className="mb-3 h-8 w-8 text-border" />
                    <p className="text-sm text-text-muted">No tracked prompts yet.</p>
                    <p className="text-xs text-text-muted">Run prompts from the Prompts page first.</p>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto">
                    {availablePrompts.map((prompt) => {
                      const checked = selectedPromptIds.has(prompt.id)
                      return (
                        <button
                          key={prompt.id}
                          onClick={() => togglePrompt(prompt.id)}
                          className={cn(
                            'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left mb-1.5 transition-all',
                            checked
                              ? 'border-brand-accent/30 bg-brand-accent/5'
                              : 'border-border hover:border-brand-accent/20 hover:bg-surface-raised'
                          )}
                        >
                          <div className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                            checked ? 'border-brand-accent bg-brand-accent' : 'border-border'
                          )}>
                            {checked && <CheckCircle className="h-3 w-3 text-white" />}
                          </div>
                          <span className={cn('flex-1 text-xs leading-snug', checked ? 'text-text' : 'text-text-muted')}>
                            {prompt.query_text}
                          </span>
                          <div className="flex shrink-0 gap-0.5">
                            {PLATFORMS.filter((p) => prompt.platforms.includes(p.id)).map((p) => (
                              <div key={p.id} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: p.color }} />
                            ))}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {trendingLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
                  </div>
                ) : (trendingData?.prompts ?? []).length === 0 ? (
                  <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
                    <Zap className="mb-3 h-8 w-8 text-border" />
                    <p className="text-sm text-text-muted">No trending queries yet.</p>
                    <p className="text-xs text-text-muted">Run more simulations to see trends.</p>
                  </div>
                ) : (
                  (trendingData?.prompts ?? []).slice(0, 15).map((q) => {
                    const alreadyTracked = availablePrompts.some((p) => p.query_text.toLowerCase() === q.queryText.toLowerCase())
                    return (
                      <div
                        key={q.queryText}
                        className="mb-1.5 flex items-center gap-2.5 rounded-lg border border-border px-3 py-2.5"
                      >
                        <span className="flex-1 text-xs text-text-muted">{q.queryText}</span>
                        {alreadyTracked ? (
                          <span className="text-[10px] font-semibold text-feedback-success">Tracked</span>
                        ) : (
                          <span className="text-[10px] font-semibold text-text-muted/40">Trending</span>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            )}

            <div className="mt-3 rounded-lg border border-border bg-surface-raised px-3 py-2.5">
              <p className="text-xs text-text-muted">
                <span className="font-bold text-brand-accent">{selectedPromptIds.size}</span> selected ·{' '}
                <span className="font-bold text-text">{scansPerRun} scans/day</span>
              </p>
            </div>
          </div>
        </StepCard>

        {/* Step 2: Schedule & Platforms */}
        <StepCard
          step={2}
          title="Schedule & Platforms"
          subtitle="When to run and which AI models to use"
          accent="linear-gradient(135deg, #20B8CD, #6366f1)"
        >
          <div className="space-y-5">
            {/* Run time */}
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-muted">Run time (UTC)</p>
              <select
                value={runTime}
                onChange={(e) => setRunTime(e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:border-brand-accent/40 focus:outline-none"
              >
                {HOUR_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="mt-1.5 text-[10px] text-text-muted">Runs once daily at this time</p>
            </div>

            {/* Platforms */}
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-muted">AI Platforms</p>
              <div className="space-y-2">
                {PLATFORMS.map((p) => {
                  const selected = selectedPlatforms.includes(p.id)
                  const locked = p.growthOnly && !isGrowthPlus
                  return (
                    <button
                      key={p.id}
                      onClick={() => togglePlatform(p.id)}
                      disabled={locked}
                      className={cn(
                        'flex w-full items-center justify-between rounded-lg border px-4 py-2.5 transition-all',
                        locked && 'cursor-not-allowed opacity-50'
                      )}
                      style={{
                        borderColor: selected ? p.color : 'var(--border)',
                        background: selected ? p.bg : 'transparent',
                      }}
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: selected ? p.color : 'var(--text-muted)' }}>
                        {p.label}
                        {locked && (
                          <span className="rounded-full border border-brand-accent/30 bg-brand-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-accent">
                            Growth+
                          </span>
                        )}
                      </span>
                      <div
                        className={cn(
                          'flex h-5 w-5 items-center justify-center rounded border transition-all',
                          selected ? 'border-current' : 'border-border'
                        )}
                        style={{ borderColor: selected ? p.color : undefined }}
                      >
                        {selected && (
                          <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
              <p className="mt-2 text-[10px] text-text-muted">
                {selectedPlatforms.length} platform{selectedPlatforms.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          </div>
        </StepCard>

        {/* Step 3: Launch */}
        <StepCard
          step={3}
          title="Launch"
          subtitle="Review and activate your automation"
          accent="linear-gradient(135deg, #10A37F, #20B8CD)"
        >
          <div className="flex h-full flex-col">
            {/* Review stats */}
            <div className="mb-4 grid grid-cols-2 gap-3">
              {[
                { label: 'PROMPTS', value: selectedPromptIds.size, color: 'text-brand-accent' },
                { label: 'PLATFORMS', value: selectedPlatforms.length, color: 'text-brand-accent' },
                { label: 'CREDITS/DAY', value: creditsPerRun > 0 ? creditsPerRun : '—', color: creditsPerRun > 0 ? 'text-text' : 'text-text-muted' },
                { label: 'DAYS LEFT', value: '∞', color: 'text-text' },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-lg border border-border bg-surface-raised p-3">
                  <p className="text-[9px] font-semibold uppercase tracking-wider text-text-muted">{label}</p>
                  <p className={cn('text-2xl font-bold', color)}>{value}</p>
                </div>
              ))}
            </div>

            {/* Credits balance */}
            <div className="mb-4 flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <span className="text-xs text-text-muted">Available balance</span>
              <span className="text-sm font-bold text-text">{stats ? `${stats.credits_used} used` : '—'}</span>
            </div>

            {/* Monthly estimate */}
            {estimatedMonthly > 0 && (
              <div className="mb-4 rounded-lg border border-border bg-surface-raised px-4 py-3">
                <p className="text-xs text-text-muted">Est. monthly scans</p>
                <p className="text-lg font-bold text-text">{estimatedMonthly.toLocaleString()}</p>
                <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-surface-overlay">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.min(100, (estimatedMonthly / 3000) * 100)}%`, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)' }}
                  />
                </div>
              </div>
            )}

            <div className="flex-1" />

            {/* Activate button */}
            <div className="space-y-2">
              <button
                onClick={handleSave}
                disabled={saveMutation.isPending || scansPerRun === 0 || !canWriteAutomation}
                title={!canWriteAutomation ? 'Only your account manager can change automation settings' : undefined}
                className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                style={{ background: scansPerRun === 0 ? 'var(--surface-raised)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
              >
                {saveMutation.isPending
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                  : saveSuccess
                  ? <><CheckCircle className="h-4 w-4" /> Saved!</>
                  : <><Zap className="h-4 w-4" /> {isActive ? 'Save Changes' : 'Save & Launch'}</>}
              </button>

              {/* Toggle */}
              {schedule && (
                <button
                  onClick={() => toggleMutation.mutate(!isActive)}
                  disabled={toggleMutation.isPending || !canWriteAutomation}
                  title={!canWriteAutomation ? 'Only your account manager can change automation settings' : undefined}
                  className={cn(
                    'flex w-full items-center justify-center gap-2 rounded-xl border py-2.5 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50',
                    isActive
                      ? 'border-feedback-error/30 bg-feedback-error/5 text-feedback-error'
                      : 'border-feedback-success/30 bg-feedback-success/5 text-feedback-success'
                  )}
                >
                  {toggleMutation.isPending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : isActive ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  {isActive ? 'Pause automation' : 'Activate automation'}
                </button>
              )}
            </div>

            {!canWriteAutomation ? (
              <p className="mt-2 text-center text-[10px] text-text-muted">
                Only your account manager can change automation settings
              </p>
            ) : scansPerRun === 0 ? (
              <p className="mt-2 text-center text-[10px] text-text-muted">
                Add at least one prompt and platform to continue
              </p>
            ) : null}
          </div>
        </StepCard>
      </div>

      {/* Bottom row: stats + history */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Automation stats */}
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          <div className="flex items-center gap-3 border-b border-border px-5 py-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-accent/10">
              <Activity className="h-4 w-4 text-brand-accent" />
            </div>
            <div>
              <p className="text-sm font-bold text-text">Automation Stats</p>
              <p className="text-[10px] text-text-muted">All time</p>
            </div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-y divide-border">
            {[
              { icon: Calendar,    label: 'Last Run',    value: formatRelative(stats?.last_run_at ?? null) },
              { icon: Zap,         label: 'Total Runs',  value: String(stats?.total_runs ?? 0) },
              { icon: Timer,       label: 'Processed',   value: String(stats?.prompts_processed ?? 0) },
              { icon: DollarSign,  label: 'Credits',     value: String(stats?.credits_used ?? 0) },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="p-4">
                <div className="mb-1 flex items-center gap-1.5">
                  <Icon className="h-3 w-3 text-brand-accent" />
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-text-muted">{label}</span>
                </div>
                <p className="text-base font-bold text-text">{value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Run history */}
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="flex items-center gap-2">
              <Timer className="h-3.5 w-3.5 text-text-muted" />
              <span className="text-sm font-bold text-text">Run History</span>
            </div>
            <button onClick={() => void refetchHistory()} className="text-text-muted hover:text-text">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="divide-y divide-border/60">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
              </div>
            ) : runs.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-text-muted">No runs yet — activate to start</p>
              </div>
            ) : (
              runs.slice(0, 8).map((run) => (
                <div key={run.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-xs font-semibold text-text">{formatDate(run.ran_at)}</p>
                    <p className="text-[11px] text-text-muted">{run.prompts_run} scans · {run.credits_used} cr</p>
                  </div>
                  <RunStatusBadge status={run.status} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
